import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export function useSignup() {
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [preferredGame, setPreferredGame] = useState("");
  const [role, setRole] = useState<"mentor" | "jogador">("jogador");
  const [loading, setLoading] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [isGoogleSignup, setIsGoogleSignup] = useState(false);

  // Buscar primeiro jogo da lista para usar como padrão
  useEffect(() => {
    const fetchFirstGame = async () => {
      try {
        const { data, error } = await supabase
          .from("games")
          .select("name")
          .order("name")
          .limit(1);

        if (!error && data && data.length > 0) {
          setPreferredGame(data[0].name);
        } else {
          // Fallback caso não consiga buscar
          setPreferredGame("League of Legends");
        }
      } catch (error) {
        console.error("Error fetching default game:", error);
        setPreferredGame("League of Legends");
      }
    };

    // Só buscar se ainda não tem jogo selecionado
    if (!preferredGame) {
      fetchFirstGame();
    }
  }, [preferredGame]);

  // Handler para Cadastro
  const handleSignup = async (e: React.FormEvent, isGoogleAuth = false) => {
    if (e) e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return false;
    
    setLoading(true);
    setIsGoogleSignup(isGoogleAuth);
    
    // Log para verificar dados antes de enviar para supabase
    console.log("Enviando para cadastro:", { email, nickname, whatsappNumber, preferredGame, role });
    
    try {
      // Cria usuário e armazena dados extras no profile
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nickname,
            whatsapp_number: whatsappNumber,
            preferred_game: preferredGame,
            role,
          }
        }
      });
      
      if (error) {
        toast({ title: "Erro no Cadastro", description: error.message, variant: "destructive" });
        console.error("Erro no cadastro:", error);
        setLoading(false);
        return false;
      }
      
      // Verificar se o usuário foi criado com sucesso
      if (!data.user || !data.user.id) {
        toast({ 
          title: "Erro no Cadastro", 
          description: "Não foi possível criar o usuário", 
          variant: "destructive" 
        });
        console.error("Usuário não criado");
        setLoading(false);
        return false;
      }

      // Para cadastro via Google, apenas mostrar toast simples
      if (isGoogleAuth) {
        toast({ 
          title: "Conta criada!", 
          description: "Bem-vindo! Sua conta foi criada com sucesso." 
        });
      } else {
        // Para cadastro manual, mostrar diálogo de verificação de email
        setRegisteredEmail(email);
        setShowEmailVerification(true);
      }
      
      // Reset form fields apenas para cadastro manual
      if (!isGoogleAuth) {
        setNickname("");
        setEmail("");
        setPassword("");
        setWhatsappNumber("");
        // Não resetar preferredGame para manter o valor padrão
        setRole("jogador");
      }
      
      setLoading(false);
      return true; // Indica sucesso para redirecionamento
      
    } catch (unexpectedError) {
      console.error("Erro inesperado no cadastro:", unexpectedError);
      toast({ 
        title: "Erro Inesperado", 
        description: "Ocorreu um erro ao processar seu cadastro. Por favor, tente novamente.", 
        variant: "destructive" 
      });
      setLoading(false);
      return false;
    }
  };

  const closeEmailVerificationDialog = () => {
    setShowEmailVerification(false);
    setRegisteredEmail("");
  };

  return {
    nickname,
    email,
    password,
    whatsappNumber,
    preferredGame,
    role,
    loading,
    showEmailVerification,
    registeredEmail,
    isGoogleSignup,
    setNickname,
    setEmail,
    setPassword,
    setWhatsappNumber,
    setPreferredGame,
    setRole,
    handleSignup,
    closeEmailVerificationDialog,
  };
}
